import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, Select } from '../../ui/components';
import { useCommercialSettings } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { SUPPLIER_STATUSES } from './suppliersApi';

const EMPTY = {
  name: '',
  code: '',
  status: 'Draft',
  email: '',
  phone: '',
  currency: '',
  taxId: '',
  paymentTermsDays: '',
};

function fromSupplier(s, defaultCurrency) {
  if (!s) return { ...EMPTY, currency: defaultCurrency };
  return {
    name: s.name ?? '',
    code: s.code ?? '',
    status: s.status ?? 'Draft',
    email: s.email ?? '',
    phone: s.phone ?? '',
    currency: s.currency ?? defaultCurrency,
    taxId: s.taxId ?? '',
    paymentTermsDays: s.paymentTermsDays ?? '',
  };
}

/**
 * Supplier create/edit form. Controlled, with light client-side validation.
 * `onSubmit(payload)` receives the cleaned payload; the parent handles the API
 * call, loading state and closing the drawer.
 */
export default function SupplierForm({ supplier, submitting, onSubmit, onCancel }) {
  const t = useT();
  const { currencies, defaultCurrency } = useCommercialSettings();
  const [form, setForm] = useState(() => fromSupplier(supplier, defaultCurrency));
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setForm(fromSupplier(supplier, defaultCurrency));
    setErrors({});
  }, [supplier, defaultCurrency]);

  const statusOptions = useMemo(
    () => SUPPLIER_STATUSES.map((s) => ({ value: s.value, label: t(s.label) })),
    [t],
  );

  // Currencies configured in Settings; keep an existing supplier's currency
  // visible even if it's outside the configured set.
  const currencyOptions = useMemo(() => {
    const codes = [...currencies];
    if (form.currency && !codes.includes(form.currency)) codes.push(form.currency);
    return codes.map((c) => ({ value: c, label: c }));
  }, [currencies, form.currency]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = t('Name is required.');
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) next.email = t('Enter a valid email.');
    if (!form.currency) next.currency = t('Select a currency.');
    if (form.paymentTermsDays !== '' && (Number(form.paymentTermsDays) < 0 || Number(form.paymentTermsDays) > 365))
      next.paymentTermsDays = t('Must be between 0 and 365.');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit({
      name: form.name.trim(),
      code: form.code.trim() || null,
      status: form.status,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      currency: form.currency.trim().toUpperCase(),
      taxId: form.taxId.trim() || null,
      paymentTermsDays: form.paymentTermsDays === '' ? null : Number(form.paymentTermsDays),
    });
  };

  return (
    <form id="supplier-form" className="pp-form" onSubmit={handleSubmit}>
      <Input
        label={t('Name')}
        placeholder="Acme Foods Ltd."
        value={form.name}
        onChange={set('name')}
        error={errors.name}
        autoFocus
      />

      <div className="pp-form__row">
        <Input label={t('Code')} placeholder="ACME-001" value={form.code} onChange={set('code')} />
        <div className="ps-field">
          <span className="ps-field__label">{t('Status')}</span>
          <Select
            value={form.status}
            onChange={(value) => setForm((f) => ({ ...f, status: value }))}
            options={statusOptions}
            ariaLabel={t('Status')}
          />
        </div>
      </div>

      <div className="pp-form__row">
        <Input
          label={t('Email')}
          type="email"
          placeholder="orders@acme.com"
          value={form.email}
          onChange={set('email')}
          error={errors.email}
        />
        <Input label={t('Phone')} placeholder="+1 555 0100" value={form.phone} onChange={set('phone')} />
      </div>

      <div className="pp-form__row">
        <div className="ps-field">
          <span className="ps-field__label">{t('Currency')}</span>
          <Select
            value={form.currency}
            onChange={(value) => setForm((f) => ({ ...f, currency: value }))}
            options={currencyOptions}
            ariaLabel={t('Currency')}
          />
          {errors.currency && <span className="ps-field__error">{errors.currency}</span>}
        </div>
        <Input
          label={t('Payment terms (days)')}
          type="number"
          placeholder="30"
          value={form.paymentTermsDays}
          onChange={set('paymentTermsDays')}
          error={errors.paymentTermsDays}
        />
      </div>

      <Input label={t('Tax ID')} placeholder="VAT / EIN" value={form.taxId} onChange={set('taxId')} />

      <div className="pp-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
          {t('Cancel')}
        </Button>
        <Button type="submit" variant="primary" loading={submitting}>
          {supplier ? t('Save changes') : t('Create supplier')}
        </Button>
      </div>
    </form>
  );
}
